import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/formatCurrency';
import { sendTelegramNotification } from '../utils/telegramNotification';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

const Checkout = () => {
  const { cartItems, getCartTotal, clearCart } = useCart();
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: userProfile?.displayName || '',
    phone: userProfile?.phone || '',
    email: currentUser?.email || '',
    address: '',
    city: '',
    district: '',
    notes: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create order in Firestore
      const orderData = {
        userId: currentUser.uid,
        customerInfo: formData,
        items: cartItems,
        totalAmount: getCartTotal(),
        status: 'pending',
        paymentMethod: 'cod',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'orders'), orderData);

      // Prepare notification data
      const notificationData = {
        orderId: docRef.id,
        customerInfo: {
          name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
        },
        items: cartItems,
        totalAmount: getCartTotal(),
        shippingAddress: `${formData.address}, ${formData.district}, ${formData.city}`,
        paymentMethod: 'cod',
      };

      // Send Telegram notification (non-blocking)
      sendTelegramNotification(notificationData).then((result) => {
        if (result.success) {
          console.log('Telegram notification sent');
        } else {
          console.warn('Failed to send Telegram notification:', result.error);
        }
      });

      // Clear cart
      clearCart();

      // Show success message
      toast.success('Đặt hàng thành công!');

      // Navigate to success page
      navigate('/order-success', { state: { orderId: docRef.id } });
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Đặt hàng thất bại. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold mb-8">Thanh Toán</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold mb-6">Thông Tin Giao Hàng</h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Họ Và Tên *
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      title="Vui lòng nhập họ và tên"
                      onInvalid={(e) => e.target.setCustomValidity('Vui lòng nhập họ và tên')}
                      onInput={(e) => e.target.setCustomValidity('')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Số Điện Thoại *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      pattern="[0-9]{10,11}"
                      title="Vui lòng nhập số điện thoại hợp lệ (10-11 số)"
                      onInvalid={(e) => e.target.setCustomValidity('Vui lòng nhập số điện thoại hợp lệ (10-11 số)')}
                      onInput={(e) => e.target.setCustomValidity('')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    title="Vui lòng nhập địa chỉ email hợp lệ"
                    onInvalid={(e) => e.target.setCustomValidity('Vui lòng nhập địa chỉ email hợp lệ')}
                    onInput={(e) => e.target.setCustomValidity('')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Địa Chỉ *
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    title="Vui lòng nhập địa chỉ giao hàng"
                    onInvalid={(e) => e.target.setCustomValidity('Vui lòng nhập địa chỉ giao hàng')}
                    onInput={(e) => e.target.setCustomValidity('')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Số nhà, tên đường, phường/xã"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quận/Huyện *
                    </label>
                    <input
                      type="text"
                      name="district"
                      value={formData.district}
                      onChange={handleChange}
                      required
                      title="Vui lòng nhập quận/huyện"
                      onInvalid={(e) => e.target.setCustomValidity('Vui lòng nhập quận/huyện')}
                      onInput={(e) => e.target.setCustomValidity('')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Thành Phố *
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      title="Vui lòng nhập thành phố"
                      onInvalid={(e) => e.target.setCustomValidity('Vui lòng nhập thành phố')}
                      onInput={(e) => e.target.setCustomValidity('')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ghi Chú Đơn Hàng (Tùy Chọn)
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Any special instructions..."
                  ></textarea>
                </div>

                <div className="border-t pt-6">
                  <h3 className="font-bold mb-3">Phương Thức Thanh Toán</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center gap-3">
                      <input type="radio" id="cod" name="payment" checked readOnly />
                      <label htmlFor="cod" className="font-medium">
                        Thanh Toán Khi Nhận Hàng (COD)
                      </label>
                    </div>
                    <p className="text-sm text-gray-600 mt-2 ml-7">
                      Thanh toán bằng tiền mặt khi nhận hàng
                    </p>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Đang đặt hàng...' : 'Đặt Hàng'}
                </button>
              </form>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
              <h2 className="text-xl font-bold mb-4">Tóm Tắt Đơn Hàng</h2>

              <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                {cartItems.map(item => (
                  <div key={item.id} className="flex gap-3">
                    <img
                      src={item.imageUrl || 'https://via.placeholder.com/60'}
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.name}</p>
                      <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                      <p className="text-sm font-semibold text-primary-600">
                        {formatCurrency(item.price * item.quantity)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-gray-600">
                  <span>Tạm Tính</span>
                  <span>{formatCurrency(getCartTotal())}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Vận Chuyển</span>
                  <span className="text-green-600">Miễn Phí</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-bold text-lg">
                  <span>Tổng Cộng</span>
                  <span className="text-primary-600">{formatCurrency(getCartTotal())}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
